import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Mail, MapPin, Phone, RefreshCw,
  X, ChevronLeft, ChevronRight, Users, Zap, Shield,
  ThumbsDown, Layers, AlertTriangle,
  UserCheck, UserX, Clock, Search
} from "lucide-react";
import RDCandidateSearchInput from "./RDCandidateSearchInput";

/* 
   ALL ORIGINAL LOGIC  PRESERVED EXACTLY
 */

const tokenizeQuery = (query) => {
  const tokens = [];
  const re = /"([^"]+)"|\(|\)|\bAND\b|\bOR\b|\bNOT\b|[^\s()]+/gi;
  let match = re.exec(query);
  while (match) {
    tokens.push(match[1] || match[0]);
    match = re.exec(query);
  }
  return tokens;
};

const isOperator = (token) => ["AND", "OR", "NOT"].includes(String(token || "").toUpperCase());

const isOperandLike = (token) => {
  if (!token) return false;
  const t = String(token).toUpperCase();
  return t !== "AND" && t !== "OR" && t !== "NOT" && token !== "(" && token !== ")";
};

const buildBooleanTokens = (query) => {
  const raw = tokenizeQuery(query).map((token) => {
    const normalized = String(token).trim();
    const upper = normalized.toUpperCase();
    if (upper === "AND" || upper === "OR" || upper === "NOT") return upper;
    return normalized;
  }).filter(Boolean);
  if (raw.length === 0) return [];
  const expanded = [];
  for (let i = 0; i < raw.length; i += 1) {
    const current = raw[i];
    const prev = expanded[expanded.length - 1];
    const needsImplicitAnd = (
      (isOperandLike(prev) || prev === ")") &&
      (isOperandLike(current) || current === "(" || current === "NOT")
    );
    if (needsImplicitAnd) expanded.push("AND");
    expanded.push(current);
  }
  return expanded;
};

const evaluateBooleanQuery = (query, text) => {
  const tokens = buildBooleanTokens(query);
  if (tokens.length === 0) return true;
  const precedence = { OR: 1, AND: 2, NOT: 3 };
  const output = [];
  const operators = [];
  tokens.forEach((token) => {
    if (token === "(") { operators.push(token); return; }
    if (token === ")") {
      while (operators.length > 0 && operators[operators.length - 1] !== "(") output.push(operators.pop());
      if (operators[operators.length - 1] === "(") operators.pop();
      return;
    }
    if (isOperator(token)) {
      while (operators.length > 0 && isOperator(operators[operators.length - 1]) &&
        precedence[operators[operators.length - 1]] >= precedence[token]) output.push(operators.pop());
      operators.push(token);
      return;
    }
    output.push(token);
  });
  while (operators.length > 0) output.push(operators.pop());
  const source = String(text || "").toLowerCase();
  const stack = [];
  for (let i = 0; i < output.length; i += 1) {
    const token = output[i];
    if (token === "NOT") { stack.push(!Boolean(stack.pop())); continue; }
    if (token === "AND" || token === "OR") {
      const right = Boolean(stack.pop());
      const left = Boolean(stack.pop());
      stack.push(token === "AND" ? left && right : left || right);
      continue;
    }
    stack.push(source.includes(String(token).toLowerCase()));
  }
  return stack.length ? Boolean(stack[stack.length - 1]) : true;
};

const tokenizeSimpleTerms = (query) => {
  const re = /"([^"]+)"|[^\s]+/g;
  const terms = [];
  let match = re.exec(query);
  while (match) { terms.push((match[1] || match[0]).trim()); match = re.exec(query); }
  return terms.filter(Boolean);
};

const isCandidateOnBench = (candidate) => {
  if (!candidate) return false;
  const raw = candidate?.is_candidate_on_bench ?? candidate?.isCandidateOnBench ??
    candidate?.candidateInfo?.is_candidate_on_bench ?? candidate?.candidateInfo?.isCandidateOnBench;
  const normalized = String(raw ?? "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return true;
  return String(candidate?.status ?? "").trim().toLowerCase() === "on bench";
};

const isCandidateInactive = (candidate) => {
  const statusLower = String(candidate?.status || "").trim().toLowerCase();
  const activeRaw = String(candidate?.is_active ?? candidate?.isActive ?? "").trim().toLowerCase();
  return statusLower === "inactive" || ["0", "false", "no"].includes(activeRaw);
};

const isCandidateActive = (candidate) => !isCandidateInactive(candidate);

const KPI_FILTERS = {
  ALL: "all",
  ACTIVE: "active",
  INACTIVE: "inactive",
};

/* 
   UI HELPERS
 */

function Avatar({ name, inactive, size = "md" }) {
  const initials = (name || "?").split(" ").slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("");
  const palettes = [
    ["#1e3a5f", "#2563eb"],
    ["#1e3a5f", "#0891b2"],
    ["#14532d", "#16a34a"],
    ["#78350f", "#d97706"],
    ["#4c1d95", "#7c3aed"],
    ["#881337", "#e11d48"],
  ];
  const idx = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % palettes.length;
  const [from, to] = inactive ? ["#475569", "#94a3b8"] : palettes[idx];
  const sz = size === "sm" ? { width: 32, height: 32, fontSize: 11 } : { width: 38, height: 38, fontSize: 13 };
  return (
    <div style={{
      width: sz.width, height: sz.height, borderRadius: 8,
      background: `linear-gradient(135deg, ${from}, ${to})`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 800, color: "#fff", fontSize: sz.fontSize,
      letterSpacing: "0.03em", flexShrink: 0,
      boxShadow: inactive ? "none" : `0 2px 8px ${to}44`,
    }}>
      {initials}
    </div>
  );
}

function StatusChip({ status, inactive }) {
  if (inactive) return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      <UserX style={{ width: 9, height: 9 }} />INACTIVE
    </span>
  );
  const s = String(status || "").toLowerCase();
  if (s === "on bench") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      <Layers style={{ width: 9, height: 9 }} />ON BENCH
    </span>
  );
  if (s === "available") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      <UserCheck style={{ width: 9, height: 9 }} />AVAILABLE
    </span>
  );
  if (s === "active") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      <UserCheck style={{ width: 9, height: 9 }} />ACTIVE
    </span>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0", letterSpacing: "0.04em", textTransform: "uppercase" }}>
      <Clock style={{ width: 9, height: 9 }} />{status || "N/A"}
    </span>
  );
}

/* 
   CANDIDATE ROW  single-line dense layout
 */

const RD_ALL_CANDIDATES_GRID_COLUMNS =
  "38px minmax(220px, 1fr) minmax(130px, 150px) minmax(210px, 240px) minmax(120px, 135px) minmax(210px, 220px)";
const RD_MOBILE_BREAKPOINT = 900;
const getViewportWidth = () =>
  typeof window === "undefined" ? 1200 : window.innerWidth;

function CandidateRow({
  candidate,
  index,
  moving,
  onBench,
  isInactive,
  onBenchClick,
  onNotInterested,
  isMobile,
}) {
  const phone    = candidate?.candidateInfo?.phoneNumber || candidate?.phoneNumber || "—";
  const email    = candidate?.candidateInfo?.emailId     || candidate?.emailId     || "—";
  const workAuth = candidate?.workAuth || candidate?.candidateInfo?.workAuthorization || "—";
  if (isMobile) {
    return (
      <div
        className="rd-row"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "14px 16px",
          background: isInactive ? "#fafafa" : "#fff",
          borderBottom: "1px solid #f1f5f9",
          opacity: isInactive ? 0.82 : 1,
          transition: "background 0.15s",
          animation: `rowIn 0.3s ease ${index * 30}ms both`,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <Avatar name={candidate?.name} inactive={isInactive} size="sm" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: isInactive ? "#94a3b8" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                {candidate?.name || "Unknown"}
              </span>
              <StatusChip status={candidate?.status} inactive={isInactive} />
              {onBench && !isInactive && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 800, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", letterSpacing: "0.06em" }}>
                  BENCH
                </span>
              )}
            </div>

            <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 500, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {candidate?.title || "—"}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              {candidate?.location && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "#0ea5e9", fontWeight: 600, lineHeight: 1 }}>
                  <MapPin style={{ width: 10, height: 10, display: "block" }} />{candidate.location}
                </span>
              )}
              {candidate?.client && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "#6366f1", fontWeight: 600, lineHeight: 1 }}>
                  <Shield style={{ width: 10, height: 10, display: "block" }} />{candidate.client}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#0369a1,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Phone style={{ width: 11, height: 11, color: "#fff" }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#334155", fontVariantNumeric: "tabular-nums", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>
              {phone}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#7c3aed,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Mail style={{ width: 11, height: 11, color: "#fff" }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#334155", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>
              {email}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#059669,#34d399)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Zap style={{ width: 11, height: 11, color: "#fff" }} />
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: "#065f46", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>
              {workAuth}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            disabled={moving}
            onClick={onBenchClick}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              padding: "7px 10px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: moving ? "not-allowed" : "pointer",
              opacity: moving ? 0.6 : 1,
              transition: "all 0.15s",
              border: "1px solid",
              flex: 1,
              ...(onBench
                ? { background: "#fffbeb", borderColor: "#fde68a", color: "#b45309" }
                : { background: "#f5f3ff", borderColor: "#ddd6fe", color: "#5b21b6" }),
            }}
          >
            {moving ? <RefreshCw style={{ width: 11, height: 11 }} className="spin" /> : <Layers style={{ width: 11, height: 11 }} />}
            {moving ? "¦" : onBench ? "Remove" : "Bench"}
          </button>

          <button
            type="button"
            disabled={moving}
            onClick={onNotInterested}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              padding: "7px 10px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: moving ? "not-allowed" : "pointer",
              opacity: moving ? 0.6 : 1,
              transition: "all 0.15s",
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              color: "#be123c",
              flex: 1,
            }}
          >
            <ThumbsDown style={{ width: 11, height: 11 }} />
            Not Interested
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="rd-row"
      style={{
        display: "grid",
        gridTemplateColumns: RD_ALL_CANDIDATES_GRID_COLUMNS,
        alignItems: "center",
        gap: "0 16px",
        padding: "22px 18px",
        background: isInactive ? "#fafafa" : "#fff",
        borderBottom: "1px solid #f1f5f9",
        opacity: isInactive ? 0.82 : 1,
        transition: "background 0.15s",
        animation: `rowIn 0.3s ease ${index * 30}ms both`,
        cursor: "default",
      }}
    >
      {/* Avatar */}
      <Avatar name={candidate?.name} inactive={isInactive} />

      {/* Name + title + badges */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 13.5, color: isInactive ? "#94a3b8" : "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }}>
            {candidate?.name || "Unknown"}
          </span>
          <StatusChip status={candidate?.status} inactive={isInactive} />
          {onBench && !isInactive && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 800, background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", letterSpacing: "0.06em" }}>
              BENCH
            </span>
          )}
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180, display: "block" }}>
            {candidate?.title || "—"}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
            {candidate?.location && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "#0ea5e9", fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1 }}>
                <MapPin style={{ width: 10, height: 10, display: "block" }} />{candidate.location}
              </span>
            )}
            {candidate?.client && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, color: "#6366f1", fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1 }}>
                <Shield style={{ width: 10, height: 10, display: "block" }} />{candidate.client}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Phone */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 130 }}>
        <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#0369a1,#38bdf8)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Phone style={{ width: 11, height: 11, color: "#fff" }} />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#334155", fontVariantNumeric: "tabular-nums", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>{phone}</span>
      </div>

      {/* Email */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 170 }}>
        <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#7c3aed,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Mail style={{ width: 11, height: 11, color: "#fff" }} />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "#334155", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>{email}</span>
      </div>

      {/* Work Auth */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 110 }}>
        <div style={{ width: 22, height: 22, borderRadius: 5, background: "linear-gradient(135deg,#059669,#34d399)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Zap style={{ width: 11, height: 11, color: "#fff" }} />
        </div>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#065f46", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}>{workAuth}</span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          disabled={moving}
          onClick={onBenchClick}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px",
            borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: moving ? "not-allowed" : "pointer",
            opacity: moving ? 0.6 : 1, transition: "all 0.15s", border: "1px solid",
            ...(onBench
              ? { background: "#fffbeb", borderColor: "#fde68a", color: "#b45309" }
              : { background: "#f5f3ff", borderColor: "#ddd6fe", color: "#5b21b6" }),
          }}
        >
          {moving ? <RefreshCw style={{ width: 11, height: 11 }} className="spin" /> : <Layers style={{ width: 11, height: 11 }} />}
          {moving ? "¦" : onBench ? "Remove" : "Bench"}
        </button>

        <button
          type="button"
          disabled={moving}
          onClick={onNotInterested}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 11px",
            borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: moving ? "not-allowed" : "pointer",
            opacity: moving ? 0.6 : 1, transition: "all 0.15s",
            background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c",
          }}
        >
          <ThumbsDown style={{ width: 11, height: 11 }} />
          Not Interested
        </button>
      </div>
    </div>
  );
}

/* 
   MAIN COMPONENT
 */

const RDAllCandidatesSection = ({
  candidates = [],
  dataLoading = false,
  searchTerm = "",
  setSearchTerm = () => {},
  currentUser = {},
  setRdCandidates = () => {},
  setSelectedRdCandidate = () => {},
  setRdSidebarCandidate = () => {},
  rdApiBaseUrl = "https://candi.vdpl.co/api/index.php/",
  onBenchUpdated = () => {},
  onNotInterestedUpdated = () => {},
}) => {
  const pageSize = 20;
  const [workingCandidateIds, setWorkingCandidateIds] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth);
  const [notInterestedDialog, setNotInterestedDialog] = useState({ open: false, candidateId: "", candidateName: "", updatedBy: "" });
  const [moveToBenchDialog, setMoveToBenchDialog] = useState({ open: false, candidateId: "", candidateName: "" });
  const [notInterestedReason, setNotInterestedReason] = useState("");
  const [notInterestedConnect, setNotInterestedConnect] = useState("");
  const [notInterestedConnectedVia, setNotInterestedConnectedVia] = useState("");
  const [notInterestedError, setNotInterestedError] = useState("");
  const [activeKpiFilter, setActiveKpiFilter] = useState(KPI_FILTERS.ALL);
  const isMobile = viewportWidth <= RD_MOBILE_BREAKPOINT;

  useEffect(() => {
    const handleResize = () => setViewportWidth(getViewportWidth());
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hasBooleanOperators = useMemo(() => /\b(AND|OR|NOT)\b|[()]/i.test(searchTerm || ""), [searchTerm]);

  const apiBaseUrl = useMemo(() => {
    const base = String(rdApiBaseUrl || "https://candi.vdpl.co/api/index.php/").trim();
    return base.endsWith("/") ? base : `${base}/`;
  }, [rdApiBaseUrl]);

  const moveCandidateToBenchEndpoint    = `${apiBaseUrl}moveCandidateToBench`;
  const removeCandidateFromBenchEndpoint = `${apiBaseUrl}removeCandidateFromBench`;
  const setCandidateEngagementEndpoint  = `${apiBaseUrl}setCandidateEngagement`;

  const searchFilteredCandidates = useMemo(() => {
    const query = String(searchTerm || "").trim();
    if (!query) return candidates;
    return candidates.filter((candidate) => {
      const haystack = [
        candidate?.name, candidate?.title, candidate?.status, candidate?.location,
        candidate?.client, candidate?.availability, candidate?.availabilityStatus,
        candidate?.experienceYears,
        ...(Array.isArray(candidate?.skills) ? candidate.skills : []),
      ].filter(Boolean).join(" ").toLowerCase();
      if (hasBooleanOperators) return evaluateBooleanQuery(query, haystack);
      const terms = tokenizeSimpleTerms(query);
      return terms.every((term) => haystack.includes(term.toLowerCase()));
    });
  }, [candidates, searchTerm, hasBooleanOperators]);

  const filteredCandidates = useMemo(() => {
    if (activeKpiFilter === KPI_FILTERS.ACTIVE) {
      return searchFilteredCandidates.filter(isCandidateActive);
    }
    if (activeKpiFilter === KPI_FILTERS.INACTIVE) {
      return searchFilteredCandidates.filter(isCandidateInactive);
    }
    return searchFilteredCandidates;
  }, [activeKpiFilter, searchFilteredCandidates]);

  const handleKpiFilterClick = useCallback((nextFilter) => {
    setCurrentPage(1);
    setActiveKpiFilter((prev) => (
      prev === nextFilter && nextFilter !== KPI_FILTERS.ALL ? KPI_FILTERS.ALL : nextFilter
    ));
  }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredCandidates.length / pageSize)), [filteredCandidates.length]);

  const paginatedCandidates = useMemo(() => {
    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    return filteredCandidates.slice((safePage - 1) * pageSize, safePage * pageSize);
  }, [currentPage, filteredCandidates, totalPages]);

  const withCandidateUpdate = useCallback((candidateId, updater) => {
    setRdCandidates((prev) => Array.isArray(prev) ? prev.map((candidate) => {
      const id = String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "");
      return id !== candidateId ? candidate : updater(candidate);
    }) : prev);
    setSelectedRdCandidate((prev) => {
      const id = String(prev?.id ?? prev?.candidate_id ?? prev?.candidateId ?? "");
      return id !== candidateId ? prev : updater(prev);
    });
    setRdSidebarCandidate((prev) => {
      const id = String(prev?.id ?? prev?.candidate_id ?? prev?.candidateId ?? "");
      return id !== candidateId ? prev : updater(prev);
    });
  }, [setRdCandidates, setSelectedRdCandidate, setRdSidebarCandidate]);

  const handleToggleBench = useCallback(async (candidate) => {
    const candidateId = String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "");
    if (!candidateId || workingCandidateIds.has(candidateId)) return;
    const userSession = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
    const movedBy = String(currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId ?? userSession?.id ?? userSession?.user_id ?? userSession?.userId ?? "").trim();
    if (!movedBy) return;
    const currentlyOnBench = isCandidateOnBench(candidate);
    const shouldMoveToBench = !currentlyOnBench;
    const endpoint = shouldMoveToBench ? moveCandidateToBenchEndpoint : removeCandidateFromBenchEndpoint;
    setWorkingCandidateIds((prev) => new Set(prev).add(candidateId));
    try {
      const formData = new FormData();
      formData.append("candidate_id", candidateId);
      formData.append("moved_by", movedBy);
      const response = await fetch(endpoint, { method: "POST", body: formData });
      const responseText = await response.text();
      let result = null;
      try { result = responseText ? JSON.parse(responseText) : null; } catch { result = null; }
      if (!response.ok || result?.success === false || String(result?.status || "").toLowerCase() === "error")
        throw new Error(result?.message || `HTTP ${response.status}`);
      withCandidateUpdate(candidateId, (item) => {
        if (!item) return item;
        const nextOnBench = shouldMoveToBench;
        const nextStatus = nextOnBench ? "On Bench" : (String(item?.status || "").toLowerCase() === "on bench" ? "Available" : item?.status);
        return { ...item, status: nextStatus, is_candidate_on_bench: nextOnBench ? "1" : "0", isCandidateOnBench: nextOnBench,
          candidateInfo: { ...(item?.candidateInfo || {}), is_candidate_on_bench: nextOnBench ? "1" : "0", isCandidateOnBench: nextOnBench } };
      });
      await Promise.resolve(onBenchUpdated?.());
    } catch (_error) {
    } finally {
      setWorkingCandidateIds((prev) => { const next = new Set(prev); next.delete(candidateId); return next; });
    }
  }, [currentUser?.id, currentUser?.user_id, currentUser?.userId, moveCandidateToBenchEndpoint, onBenchUpdated, removeCandidateFromBenchEndpoint, withCandidateUpdate, workingCandidateIds]);

  const handleNotInterested = useCallback((candidate) => {
    const candidateId = String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "");
    if (!candidateId || workingCandidateIds.has(candidateId)) return;
    const userSession = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
    const updatedBy = String(currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId ?? userSession?.id ?? userSession?.user_id ?? userSession?.userId ?? "").trim();
    if (!updatedBy) return;
    setNotInterestedReason(""); setNotInterestedConnect(""); setNotInterestedConnectedVia(""); setNotInterestedError("");
    setNotInterestedDialog({ open: true, candidateId, candidateName: String(candidate?.name || "Candidate"), updatedBy });
  }, [currentUser?.id, currentUser?.user_id, currentUser?.userId, workingCandidateIds]);

  const submitNotInterested = useCallback(async () => {
    const candidateId = String(notInterestedDialog?.candidateId || "");
    if (!candidateId) return;
    const reason = String(notInterestedReason || "").trim();
    const candidateConnect = String(notInterestedConnect || "").trim();
    const connectedVia = String(notInterestedConnectedVia || "").trim();
    if (!candidateConnect) { setNotInterestedError("Candidate Connect is required."); return; }
    if (!connectedVia) { setNotInterestedError("Connected Via is required."); return; }
    if (!reason) { setNotInterestedError("Candidate reason is required."); return; }
    setNotInterestedError("");
    setWorkingCandidateIds((prev) => new Set(prev).add(candidateId));
    try {
      const body = new URLSearchParams();
      body.append("candidate_id", candidateId);
      body.append("candidate_connect", candidateConnect);
      body.append("connected_via", connectedVia);
      body.append("candidate_response", "Not Interested");
      body.append("candidate_comments", reason);
      body.append("updated_by", String(notInterestedDialog?.updatedBy || ""));
      const response = await fetch(setCandidateEngagementEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let result = null;
      try { result = responseText ? JSON.parse(responseText) : null; } catch { result = null; }
      if (!response.ok || result?.success === false || String(result?.status || "").toLowerCase() === "error")
        throw new Error(result?.message || `HTTP ${response.status}`);
      withCandidateUpdate(candidateId, (item) => {
        if (!item) return item;
        return { ...item, candidate_response: "Not Interested", candidateResponse: "Not Interested",
          candidate_comments: reason, comments: reason,
          candidateInfo: { ...(item?.candidateInfo || {}), candidate_response: "Not Interested",
            candidateResponse: "Not Interested", candidate_comments: reason, comments: reason } };
      });
      await Promise.resolve(onNotInterestedUpdated?.());
      setNotInterestedDialog({ open: false, candidateId: "", candidateName: "", updatedBy: "" });
      setNotInterestedReason(""); setNotInterestedConnect(""); setNotInterestedConnectedVia(""); setNotInterestedError("");
    } catch (_error) {
      setNotInterestedError("Unable to submit. Please try again.");
    } finally {
      setWorkingCandidateIds((prev) => { const next = new Set(prev); next.delete(candidateId); return next; });
    }
  }, [notInterestedConnect, notInterestedConnectedVia, notInterestedDialog?.candidateId, notInterestedDialog?.updatedBy, notInterestedReason, onNotInterestedUpdated, setCandidateEngagementEndpoint, withCandidateUpdate]);

  const goToPreviousPage = useCallback(() => setCurrentPage((prev) => Math.max(1, prev - 1)), []);
  const goToNextPage = useCallback(() => setCurrentPage((prev) => Math.min(totalPages, prev + 1)), [totalPages]);
  const closeMoveToBenchDialog = useCallback(() => setMoveToBenchDialog({ open: false, candidateId: "", candidateName: "" }), []);

  const handleMoveToBenchClick = useCallback((candidate) => {
    const candidateId = String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "");
    if (!candidateId || workingCandidateIds.has(candidateId)) return;
    if (isCandidateOnBench(candidate)) { handleToggleBench(candidate); return; }
    setMoveToBenchDialog({ open: true, candidateId, candidateName: String(candidate?.name || "Candidate") });
  }, [handleToggleBench, workingCandidateIds]);

  const confirmMoveToBench = useCallback(async () => {
    const candidateId = String(moveToBenchDialog?.candidateId || "");
    if (!candidateId) return;
    const selected = candidates.find((item) => {
      const id = String(item?.id ?? item?.candidate_id ?? item?.candidateId ?? "");
      return id === candidateId;
    });
    if (!selected) { closeMoveToBenchDialog(); return; }
    await handleToggleBench(selected);
    closeMoveToBenchDialog();
  }, [candidates, closeMoveToBenchDialog, handleToggleBench, moveToBenchDialog?.candidateId]);

  const activeCnt = useMemo(
    () => searchFilteredCandidates.filter(isCandidateActive).length,
    [searchFilteredCandidates]
  );
  const inactiveCnt = useMemo(
    () => searchFilteredCandidates.filter(isCandidateInactive).length,
    [searchFilteredCandidates]
  );

  const safePage  = Math.min(Math.max(currentPage, 1), totalPages);
  const showStart = filteredCandidates.length ? (safePage - 1) * pageSize + 1 : 0;
  const showEnd   = Math.min(safePage * pageSize, filteredCandidates.length);

  /* 
     RENDER
   */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .rd2-root { font-family: 'IBM Plex Sans', sans-serif; box-sizing: border-box; }
        .rd2-root * { box-sizing: border-box; }
        .rd2-mono { font-family: 'IBM Plex Mono', monospace !important; }

        @keyframes fadeDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes rowIn    { from { opacity:0; transform:translateX(-4px); } to { opacity:1; transform:translateX(0); } }
        @keyframes modalIn  { from { opacity:0; transform:scale(0.96) translateY(6px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes spin     { to { transform:rotate(360deg); } }
        @keyframes shimmer  { 0%,100%{opacity:.45} 50%{opacity:.85} }

        .spin { animation: spin 0.8s linear infinite; }
        .rd2-row:hover { background: #f8faff !important; }
        .rd2-row.inactive:hover { background: #fef9f9 !important; }

        .rd2-btn-bench:hover:not(:disabled) { background: #ede9fe !important; border-color:#c4b5fd !important; transform:translateY(-1px); box-shadow:0 2px 8px #7c3aed18; }
        .rd2-btn-ni:hover:not(:disabled)    { background: #ffe4e6 !important; border-color:#fda4af !important; transform:translateY(-1px); box-shadow:0 2px 8px #e1183018; }

        select { -webkit-appearance:none; appearance:none; }
      `}</style>

      <div className="rd2-root" style={{ display: "flex", flexDirection: "column", gap: 0 }}>

        {/* Pagination  */}
        <div style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: "12px 12px 0 0",
          padding: "14px 20px 12px",
          animation: "fadeDown 0.4s ease both",
          borderBottom: "none",
        }}>
          {/* Top row: title + actions */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            {/* Title block */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "linear-gradient(135deg,#1e3a8a,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px #3b82f640" }}>
                <Users style={{ width: 17, height: 17, color: "#fff" }} />
              </div>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0, letterSpacing: "-0.01em" }}>All RD Candidates</h3>
                <p style={{ fontSize: 10.5, color: "#94a3b8", margin: 0, marginTop: 1, fontWeight: 500 }}>
                  Boolean: <code className="rd2-mono" style={{ fontSize: 10, background: "#f1f5f9", padding: "1px 5px", borderRadius: 3, color: "#6366f1" }}>AND</code>{" "}
                  <code className="rd2-mono" style={{ fontSize: 10, background: "#f1f5f9", padding: "1px 5px", borderRadius: 3, color: "#6366f1" }}>OR</code>{" "}
                  <code className="rd2-mono" style={{ fontSize: 10, background: "#f1f5f9", padding: "1px 5px", borderRadius: 3, color: "#6366f1" }}>NOT</code>
                </p>
              </div>
            </div>

            {/* Right controls */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* Search */}
              <RDCandidateSearchInput
                variant="all"
                value={searchTerm}
                onChange={(v) => { setSearchTerm(v); setCurrentPage(1); }}
                placeholder='"java" AND (react OR angular) NOT intern'
              />
            </div>
          </div>

          {/* Stat strip */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, rowGap: 12, marginTop: 12, paddingTop: 10, borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
            {[
              { key: KPI_FILTERS.ALL, label: "Total Candidates", value: searchFilteredCandidates.length, color: "#1e40af", bg: "#eff6ff", border: "#bfdbfe" },
              { key: KPI_FILTERS.ACTIVE, label: "Active", value: activeCnt, color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
              { key: KPI_FILTERS.INACTIVE, label: "Inactive", value: inactiveCnt, color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
            ].map(({ key, label, value, color, bg, border }) => {
              const isSelected = activeKpiFilter === key;
              return (
                <button
                  key={label}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => handleKpiFilterClick(key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 12px",
                    borderRadius: 8,
                    background: bg,
                    border: `1px solid ${border}`,
                    cursor: "pointer",
                    boxShadow: isSelected ? `0 0 0 2px ${color}22` : "none",
                    transform: isSelected ? "translateY(-1px)" : "none",
                    transition: "all 0.15s ease",
                  }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{value}</span>
                  <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600 }}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {!isMobile && (
          <>
            {/* TABLE HEADER */}
            <div style={{
              display: "grid",
              gridTemplateColumns: RD_ALL_CANDIDATES_GRID_COLUMNS,
              alignItems: "center",
              gap: "0 16px",
              padding: "7px 16px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderTop: "1px solid #e2e8f0",
              borderBottom: "none",
            }}>
              {["", "Candidate", "Phone", "Email", "Work Auth", "Actions"].map((col, i) => (
                <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", justifySelf: "start" }}>{col}</span>
              ))}
            </div>
          </>
        )}

        {/*  BODY  */}
        <div style={{
          border: "1px solid #e2e8f0",
          borderRadius: "0 0 12px 12px",
          overflowX: "auto",
          overflowY: "hidden",
          background: "#fff",
        }}>
          {dataLoading ? (
            <div style={{ padding: 0 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} style={{
                  height: 56, borderBottom: "1px solid #f1f5f9",
                  animation: `shimmer 1.4s ease ${i * 150}ms infinite`,
                  background: "linear-gradient(90deg,#f8fafc 0%,#f1f5f9 50%,#f8fafc 100%)",
                }} />
              ))}
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div style={{ padding: "56px 24px", textAlign: "center" }}>
              <Search style={{ width: 32, height: 32, color: "#cbd5e1", margin: "0 auto 10px" }} />
              <p style={{ fontSize: 14, fontWeight: 700, color: "#64748b", margin: 0 }}>No candidates found</p>
              <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 0", fontWeight: 500 }}>Adjust your search or filter</p>
            </div>
          ) : (
            <>
              {paginatedCandidates.map((candidate, index) => {
                const candidateId = String(candidate?.id ?? candidate?.candidate_id ?? candidate?.candidateId ?? "");
                const moving      = workingCandidateIds.has(candidateId);
                const onBench     = isCandidateOnBench(candidate);
                const isInactive  = isCandidateInactive(candidate);
                return (
                  <CandidateRow
                    key={candidate?.id || `${candidate?.name}-${index}`}
                    candidate={candidate}
                    index={index}
                    moving={moving}
                    onBench={onBench}
                    isInactive={isInactive}
                    isMobile={isMobile}
                    onBenchClick={() => handleMoveToBenchClick(candidate)}
                    onNotInterested={() => handleNotInterested(candidate)}
                  />
                );
              })}

              {/* Pagination  */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 16px", borderTop: "1px solid #e2e8f0",
                background: "#fafafa", flexWrap: "wrap", gap: 8,
              }}>
                <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>
                  Showing <strong style={{ color: "#0f172a" }}>{showStart}-{showEnd}</strong> of{" "}
                  <strong style={{ color: "#0f172a" }}>{filteredCandidates.length}</strong> candidates
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button type="button" onClick={goToPreviousPage} disabled={currentPage <= 1}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, color: currentPage <= 1 ? "#cbd5e1" : "#374151", background: "#fff", border: `1px solid ${currentPage <= 1 ? "#f1f5f9" : "#e2e8f0"}`, cursor: currentPage <= 1 ? "not-allowed" : "pointer", transition: "all 0.15s" }}>
                    <ChevronLeft style={{ width: 13, height: 13 }} />Prev
                  </button>
                  <div style={{ padding: "5px 14px", borderRadius: 6, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#1d4ed8" }}>{safePage}</span>
                    <span style={{ fontSize: 11, color: "#93c5fd", fontWeight: 500 }}> / {totalPages}</span>
                  </div>
                  <button type="button" onClick={goToNextPage} disabled={currentPage >= totalPages}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 11px", borderRadius: 6, fontSize: 11.5, fontWeight: 700, color: currentPage >= totalPages ? "#cbd5e1" : "#374151", background: "#fff", border: `1px solid ${currentPage >= totalPages ? "#f1f5f9" : "#e2e8f0"}`, cursor: currentPage >= totalPages ? "not-allowed" : "pointer", transition: "all 0.15s" }}>
                    Next<ChevronRight style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/*  MOVE TO BENCH MODAL */}
        {moveToBenchDialog.open && (
          <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)" }}>
            <div style={{ width: "100%", maxWidth: 380, borderRadius: 12, overflow: "hidden", background: "#fff", animation: "modalIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both", border: "1px solid #e2e8f0", boxShadow: "0 20px 60px -10px #00000030" }}>
              <div style={{ height: 3, background: "linear-gradient(90deg,#1e40af,#6366f1)" }} />
              <div style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg,#1e40af,#6366f1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Layers style={{ width: 17, height: 17, color: "#fff" }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 }}>Move to Bench</h4>
                    <p style={{ fontSize: 11, color: "#94a3b8", margin: 0, fontWeight: 500 }}>Confirm placement</p>
                  </div>
                </div>
                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: "#1d4ed8", fontWeight: 600, margin: 0 }}>
                    Move <strong>{moveToBenchDialog.candidateName}</strong> to bench?
                  </p>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button type="button" onClick={closeMoveToBenchDialog} disabled={workingCandidateIds.has(moveToBenchDialog.candidateId)}
                    style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#475569", background: "#fff", border: "1px solid #e2e8f0", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="button" onClick={confirmMoveToBench} disabled={workingCandidateIds.has(moveToBenchDialog.candidateId)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 18px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#1e40af,#6366f1)", border: "none", cursor: "pointer", boxShadow: "0 4px 12px #6366f130", opacity: workingCandidateIds.has(moveToBenchDialog.candidateId) ? 0.6 : 1 }}>
                    {workingCandidateIds.has(moveToBenchDialog.candidateId) ? <><RefreshCw style={{ width: 12, height: 12 }} className="spin" />Updating¦</> : <><Layers style={{ width: 12, height: 12 }} />Confirm</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/*  NOT INTERESTED MODAL  */}
        {notInterestedDialog.open && (
          <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)" }}>
            <div style={{ width: "100%", maxWidth: 420, borderRadius: 12, overflow: "hidden", background: "#fff", animation: "modalIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both", border: "1px solid #e2e8f0", boxShadow: "0 20px 60px -10px #00000030" }}>
              <div style={{ height: 3, background: "linear-gradient(90deg,#be123c,#f43f5e)" }} />
              <div style={{ padding: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg,#be123c,#f43f5e)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <ThumbsDown style={{ width: 17, height: 17, color: "#fff" }} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: 10, fontWeight: 700, color: "#0f172a", margin: 0 }}>Not Interested</h4>
                      <p style={{ fontSize: 11, color: "#94a3b8", margin: 0, fontWeight: 500, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{notInterestedDialog.candidateName}</p>
                    </div>
                  </div>
                  <button onClick={() => { if (workingCandidateIds.has(notInterestedDialog.candidateId)) return; setNotInterestedDialog({ open: false, candidateId: "", candidateName: "", updatedBy: "" }); setNotInterestedReason(""); setNotInterestedConnect(""); setNotInterestedConnectedVia(""); setNotInterestedError(""); }}
                    style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b" }}>
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Candidate Connect */}
                  <div>
                    <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Candidate Connect</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {["Yes", "No"].map((v) => (
                        <button key={v} type="button" onClick={() => setNotInterestedConnect(v)}
                          style={{ padding: "8px", borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", border: "1.5px solid",
                            ...(notInterestedConnect === v
                              ? { background: "#eff6ff", borderColor: "#93c5fd", color: "#1d4ed8" }
                              : { background: "#f8fafc", borderColor: "#e2e8f0", color: "#64748b" }) }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Connected Via */}
                  <div>
                    <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Connected Via</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {["Phone", "Email"].map((v) => (
                        <button key={v} type="button" onClick={() => setNotInterestedConnectedVia(v)}
                          style={{ padding: "8px", borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: "pointer", transition: "all 0.15s", border: "1.5px solid",
                            ...(notInterestedConnectedVia === v
                              ? { background: "#eff6ff", borderColor: "#93c5fd", color: "#1d4ed8" }
                              : { background: "#f8fafc", borderColor: "#e2e8f0", color: "#64748b" }) }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Comments */}
                  <div>
                    <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>Comments</label>
                    <textarea
                      rows={3}
                      value={notInterestedReason}
                      onChange={(e) => setNotInterestedReason(e.target.value)}
                      placeholder="Enter reason¦"
                      style={{ width: "100%", borderRadius: 7, border: "1.5px solid #e2e8f0", background: "#f8fafc", padding: "9px 12px", fontSize: 13, color: "#0f172a", fontWeight: 500, fontFamily: "inherit", resize: "none", outline: "none", transition: "border-color 0.15s" }}
                      onFocus={(e) => e.target.style.borderColor = "#fda4af"}
                      onBlur={(e) => e.target.style.borderColor = "#e2e8f0"}
                    />
                  </div>
                </div>

                {notInterestedError && (
                  <div style={{ display: "flex", alignItems: "center", gap: 7, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 7, padding: "8px 12px", marginTop: 10 }}>
                    <AlertTriangle style={{ width: 13, height: 13, color: "#dc2626", flexShrink: 0 }} />
                    <p style={{ fontSize: 11.5, fontWeight: 600, color: "#dc2626", margin: 0 }}>{notInterestedError}</p>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                  <button type="button" onClick={() => { if (workingCandidateIds.has(notInterestedDialog.candidateId)) return; setNotInterestedDialog({ open: false, candidateId: "", candidateName: "", updatedBy: "" }); setNotInterestedReason(""); setNotInterestedConnect(""); setNotInterestedConnectedVia(""); setNotInterestedError(""); }}
                    style={{ padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#475569", background: "#fff", border: "1px solid #e2e8f0", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="button" onClick={submitNotInterested} disabled={workingCandidateIds.has(notInterestedDialog.candidateId)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 18px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#fff", background: "linear-gradient(135deg,#be123c,#f43f5e)", border: "none", cursor: "pointer", boxShadow: "0 4px 12px #f4436030", opacity: workingCandidateIds.has(notInterestedDialog.candidateId) ? 0.6 : 1 }}>
                    {workingCandidateIds.has(notInterestedDialog.candidateId) ? <><RefreshCw style={{ width: 12, height: 12 }} className="spin" />Submitting ¦</> : <><ThumbsDown style={{ width: 12, height: 12 }} />Submit</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RDAllCandidatesSection;



